import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { hrApi } from '../api';
import type { PerformanceGoal, PerformanceGoalStatus } from '../types';

const GOAL_STATUSES: PerformanceGoalStatus[] = ['open', 'achieved', 'dropped'];

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Present when editing an existing goal; absent when creating a new one. */
  goal?: PerformanceGoal | null;
  /** Fixed employee to create the goal for (create-mode only). */
  employeeId?: string;
  employeeName?: string;
  onSaved: () => void;
}

/** Create/edit a performance goal. Create-mode targets `employeeId`;
 *  edit-mode patches the existing goal (employee is fixed, not editable). */
export function GoalDialog({ open, onOpenChange, goal, employeeId, employeeName, onSaved }: Props) {
  const isEdit = !!goal;
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [status, setStatus] = React.useState<PerformanceGoalStatus>('open');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTitle(goal?.title ?? '');
    setDescription(goal?.description ?? '');
    setDueDate(goal?.due_date ?? '');
    setStatus(goal?.status ?? 'open');
  }, [open, goal]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      if (isEdit && goal) {
        await hrApi.updateGoal(goal.id, {
          title: title.trim(),
          description: description.trim() || null,
          due_date: dueDate || null,
          status,
        });
        toast.success('Goal updated');
      } else {
        if (!employeeId) { toast.error('Pick an employee first'); setBusy(false); return; }
        await hrApi.createGoal({
          employee_id: employeeId,
          title: title.trim(),
          description: description.trim() || null,
          due_date: dueDate || null,
          status,
        });
        toast.success('Goal added');
      }
      onSaved();
    } catch (err: any) {
      toast.error(isEdit ? 'Could not update goal' : 'Could not add goal', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit goal' : `New goal${employeeName ? ` — ${employeeName}` : ''}`}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-4 pt-1">
          <div className="grid gap-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ship the Q3 onboarding revamp" required />
          </div>
          <div className="grid gap-1.5">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Optional detail or success criteria" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as PerformanceGoalStatus)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GOAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add goal'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
